import React, { useState } from 'react';
import { Box, Button, Container, Grid, Paper, Typography } from '@mui/material';
import { ChromePicker, ColorResult } from 'react-color';
import { ContentCopy, Favorite } from '@mui/icons-material';

function hexToRgb(hex: string) {
  const match = hex.replace('#', '').match(/.{1,2}/g);
  if (!match) return [0, 0, 0];
  return match.map(x => parseInt(x, 16));
}

function rgbToHsl(r: number, g: number, b: number) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  return [h * 360, s, l];
}

function calculateContrastRatio(hex1: string, hex2: string) {
  function luminance([r, g, b]: number[]) {
    const a = [r, g, b].map(v => {
      v /= 255;
      return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * a[0] + 0.7152 * a[1] + 0.0722 * a[2];
  }
  const lum1 = luminance(hexToRgb(hex1));
  const lum2 = luminance(hexToRgb(hex2));
  const lighter = Math.max(lum1, lum2);
  const darker = Math.min(lum1, lum2);
  return (lighter + 0.05) / (darker + 0.05);
}

function calculateColorSimilarity(hex1: string, hex2: string) {
  const [r1, g1, b1] = hexToRgb(hex1);
  const [r2, g2, b2] = hexToRgb(hex2);
  const deltaR = Math.abs(r1 - r2) / 255;
  const deltaG = Math.abs(g1 - g2) / 255;
  const deltaB = Math.abs(b1 - b2) / 255;
  return 1 - (deltaR + deltaG + deltaB) / 3;
}

function getAccessibilityScore(contrastRatio: number) {
  if (contrastRatio >= 7) return 100;
  if (contrastRatio >= 4.5) return 80;
  if (contrastRatio >= 3) return 60;
  if (contrastRatio >= 2) return 40;
  return 20;
}

function analyzeColorHarmony(hsl1: number[], hsl2: number[]) {
  const hueDiff = Math.abs(hsl1[0] - hsl2[0]);
  if (hueDiff <= 30) {
    return 'Monokromatik Uyum\n• Aynı rengin farklı tonları kullanılır\n• Profesyonel ve zarif bir görünüm sağlar\n• Önerilen Kullanım: Kurumsal kimlikler, minimal tasarımlar';
  }
  if (hueDiff <= 60) {
    return 'Analog Uyum\n• Renk çemberinde birbirine yakın renkler\n• Doğal ve akıcı bir görünüm sağlar\n• Önerilen Kullanım: Doğa temalı tasarımlar, yumuşak geçişler';
  }
  if (Math.abs(hueDiff - 180) <= 30) {
    return 'Tamamlayıcı Uyum\n• Renk çemberinde karşılıklı renkler\n• Yüksek kontrast ve canlılık sağlar\n• Önerilen Kullanım: Dikkat çekici başlıklar, vurgu öğeleri';
  }
  if (Math.abs(hueDiff - 120) <= 30 || Math.abs(hueDiff - 240) <= 30) {
    return 'Üçlü Uyum\n• Eşit aralıklarla yerleştirilmiş üç renk\n• Dengeli ve dinamik bir görünüm sağlar\n• Önerilen Kullanım: Modern web tasarımları, logo tasarımları';
  }
  if (Math.abs(hueDiff - 90) <= 30 || Math.abs(hueDiff - 270) <= 30) {
    return 'Dik Uyum\n• Ana renk ve tamamlayıcısının yanındaki renkler\n• Yüksek kontrast ama daha yumuşak bir görünüm\n• Önerilen Kullanım: Sanatsal tasarımlar, modern arayüzler';
  }
  return 'Uyumsuz Kombinasyon\n• Renkler arasında belirli bir uyum yok\n• Göz yorucu olabilir\n• Önerilen: Farklı renk kombinasyonları deneyin';
}

// Delta E (CIE76) - Renk farkı ölçümü
function calculateDeltaE(hex1: string, hex2: string): number {
  // RGB to LAB dönüşümü
  const rgbToLab = (red: number, green: number, blue: number) => {
    // RGB to XYZ
    let rn = red / 255, gn = green / 255, bn = blue / 255;
    rn = rn > 0.04045 ? Math.pow((rn + 0.055) / 1.055, 2.4) : rn / 12.92;
    gn = gn > 0.04045 ? Math.pow((gn + 0.055) / 1.055, 2.4) : gn / 12.92;
    bn = bn > 0.04045 ? Math.pow((bn + 0.055) / 1.055, 2.4) : bn / 12.92;

    const x = (rn * 0.4124 + gn * 0.3576 + bn * 0.1805) / 0.95047;
    const y = (rn * 0.2126 + gn * 0.7152 + bn * 0.0722) / 1.0;
    const z = (rn * 0.0193 + gn * 0.1192 + bn * 0.9505) / 1.08883;

    // XYZ to LAB
    const f = (t: number) => t > 0.008856 ? Math.pow(t, 1 / 3) : (7.787 * t) + 16 / 116;
    const L = (116 * f(y)) - 16;
    const aLab = 500 * (f(x) - f(y));
    const bLab = 200 * (f(y) - f(z));

    return [L, aLab, bLab];
  };

  const [r1, g1, b1] = hexToRgb(hex1);
  const [r2, g2, b2] = hexToRgb(hex2);
  const [L1, a1, b1Lab] = rgbToLab(r1, g1, b1);
  const [L2, a2, b2Lab] = rgbToLab(r2, g2, b2);

  return Math.sqrt(
    Math.pow(L2 - L1, 2) +
    Math.pow(a2 - a1, 2) +
    Math.pow(b2Lab - b1Lab, 2)
  );
}

// Delta E yorumlama
function getDeltaEDescription(deltaE: number): string {
  if (deltaE < 1) return 'Algılanamaz fark';
  if (deltaE < 2) return 'Çok küçük fark, yakın inceleme gerektirir';
  if (deltaE < 3.5) return 'Orta düzey fark, gözle görülebilir';
  if (deltaE < 5) return 'Belirgin fark';
  if (deltaE < 10) return 'Büyük fark';
  return 'Çok büyük fark, farklı renkler';
}

const FAVORITES_KEY = 'favorites_v1';

const ColorComparator: React.FC = () => {
  const [color1, setColor1] = useState('#ff0000');
  const [color2, setColor2] = useState('#0000ff');
  const [copied, setCopied] = useState<string | null>(null);
  const [favAdded, setFavAdded] = useState(false);

  const handleColor1Change = (color: ColorResult) => setColor1(color.hex);
  const handleColor2Change = (color: ColorResult) => setColor2(color.hex);
  const handleCopy = (text: string, which: string) => {
    navigator.clipboard.writeText(text);
    setCopied(which);
    setTimeout(() => setCopied(null), 2000);
  };

  const handleAddFavorite = () => {
    const fav = {
      id: 'compare-' + color1 + '-' + color2,
      type: 'compare',
      name: `Karşılaştırma (${color1} - ${color2})`,
      data: { color1, color2 },
      date: new Date().toISOString(),
    };
    let favs: any[] = [];
    try {
      favs = JSON.parse(localStorage.getItem(FAVORITES_KEY) || '[]');
    } catch { }
    if (!favs.some((f: any) => f.id === fav.id)) {
      favs.unshift(fav);
      localStorage.setItem(FAVORITES_KEY, JSON.stringify(favs));
      window.dispatchEvent(new Event('favoriteAdded'));
      setFavAdded(true);
      setTimeout(() => setFavAdded(false), 1500);
    }
  };

  // Detaylar
  const [r1, g1, b1] = hexToRgb(color1);
  const [r2, g2, b2] = hexToRgb(color2);
  const hsl1 = rgbToHsl(r1, g1, b1);
  const hsl2 = rgbToHsl(r2, g2, b2);
  const contrastRatio = calculateContrastRatio(color1, color2);
  const colorSimilarity = calculateColorSimilarity(color1, color2);
  const accessibilityScore = getAccessibilityScore(contrastRatio);
  const harmony = analyzeColorHarmony(hsl1, hsl2);
  const deltaE = calculateDeltaE(color1, color2);
  const deltaEDesc = getDeltaEDescription(deltaE);

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Renk Karşılaştırıcı
        </Typography>
        <Button
          variant="contained"
          color={favAdded ? 'success' : 'secondary'}
          startIcon={<Favorite />}
          onClick={handleAddFavorite}
          sx={{ mb: 2 }}
          disabled={favAdded}
        >
          {favAdded ? 'Favorilere Eklendi' : 'Favorilere Ekle'}
        </Button>
        {/* Üstte iki renk seçici */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, borderRadius: 3, boxShadow: 3 }}>
              <Typography variant="h6" sx={{ bgcolor: 'primary.main', color: 'white', borderRadius: 1, px: 2, py: 1, mb: 2, textAlign: 'center' }}>Renk 1</Typography>
              <ChromePicker color={color1} onChange={handleColor1Change} />
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, borderRadius: 3, boxShadow: 3 }}>
              <Typography variant="h6" sx={{ bgcolor: 'success.main', color: 'white', borderRadius: 1, px: 2, py: 1, mb: 2, textAlign: 'center' }}>Renk 2</Typography>
              <ChromePicker color={color2} onChange={handleColor2Change} />
            </Paper>
          </Grid>
        </Grid>
        {/* Orta: Renk detayları */}
        <Grid container spacing={3} sx={{ mt: 2 }} alignItems="stretch">
          <Grid item xs={12} md={5}>
            <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 1, bgcolor: '#f5f5f5', height: '100%' }}>
              <Box sx={{ height: 150, borderRadius: 2, mb: 2, boxShadow: 2, background: color1 }} />
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>Renk 1 Detayları</Typography>
              <Typography>RGB: {r1}, {g1}, {b1}</Typography>
              <Typography>HEX: {color1.toUpperCase()}</Typography>
              <Typography>HSL: {hsl1[0].toFixed(0)}°, {(hsl1[1] * 100).toFixed(0)}%, {(hsl1[2] * 100).toFixed(0)}%</Typography>
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                <Button size="small" startIcon={<ContentCopy />} onClick={() => handleCopy(color1, 'color1')}>
                  {copied === 'color1' ? 'Kopyalandı!' : 'Kopyala'}
                </Button>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} md={2} display="flex" alignItems="center" justifyContent="center">
            <Box sx={{ width: 2, bgcolor: '#E0E0E0', height: '100%' }} />
          </Grid>
          <Grid item xs={12} md={5}>
            <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 1, bgcolor: '#f5f5f5', height: '100%' }}>
              <Box sx={{ height: 150, borderRadius: 2, mb: 2, boxShadow: 2, background: color2 }} />
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>Renk 2 Detayları</Typography>
              <Typography>RGB: {r2}, {g2}, {b2}</Typography>
              <Typography>HEX: {color2.toUpperCase()}</Typography>
              <Typography>HSL: {hsl2[0].toFixed(0)}°, {(hsl2[1] * 100).toFixed(0)}%, {(hsl2[2] * 100).toFixed(0)}%</Typography>
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                <Button size="small" startIcon={<ContentCopy />} onClick={() => handleCopy(color2, 'color2')}>
                  {copied === 'color2' ? 'Kopyalandı!' : 'Kopyala'}
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
        {/* Alt: Karşılaştırma Sonuçları */}
        <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 1, bgcolor: '#f5f5f5', mt: 4 }}>
          <Typography variant="h6" fontWeight={700} gutterBottom>Karşılaştırma Sonuçları</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography>Kontrast Oranı: <b>{contrastRatio.toFixed(2)}:1</b></Typography>
              <Typography>Renk Benzerliği: <b>{(colorSimilarity * 100).toFixed(0)}%</b></Typography>
              <Typography>Erişilebilirlik Skoru: <b>{accessibilityScore}/100</b></Typography>
              <Typography sx={{ mt: 1 }}>Delta E (CIE76): <b>{deltaE.toFixed(2)}</b></Typography>
              <Typography color="text.secondary" fontSize={14}>{deltaEDesc}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography>Renk Uyumu:</Typography>
              <Typography sx={{ whiteSpace: 'pre-line', fontSize: 15 }}>{harmony}</Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* UI Preview */}
        <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 1, bgcolor: '#f5f5f5', mt: 4 }}>
          <Typography variant="h6" fontWeight={700} gutterBottom>UI Önizleme</Typography>
          <Grid container spacing={3}>
            {/* Metin Önizleme */}
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" gutterBottom>Metin Önizleme</Typography>
              <Box sx={{ p: 2, bgcolor: color1, borderRadius: 1 }}>
                <Typography sx={{ color: color2, fontWeight: 700 }}>Başlık Metni</Typography>
                <Typography sx={{ color: color2, fontSize: 14 }}>Bu bir örnek metin paragrafıdır.</Typography>
              </Box>
              <Box sx={{ p: 2, bgcolor: color2, borderRadius: 1, mt: 1 }}>
                <Typography sx={{ color: color1, fontWeight: 700 }}>Başlık Metni</Typography>
                <Typography sx={{ color: color1, fontSize: 14 }}>Bu bir örnek metin paragrafıdır.</Typography>
              </Box>
            </Grid>

            {/* Button Önizleme */}
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" gutterBottom>Buton Önizleme</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button sx={{ bgcolor: color1, color: color2, '&:hover': { bgcolor: color1 } }}>
                  Buton (Renk 1 Arka Plan)
                </Button>
                <Button sx={{ bgcolor: color2, color: color1, '&:hover': { bgcolor: color2 } }}>
                  Buton (Renk 2 Arka Plan)
                </Button>
                <Button variant="outlined" sx={{ borderColor: color1, color: color1 }}>
                  Outline Buton
                </Button>
              </Box>
            </Grid>

            {/* Card Önizleme */}
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" gutterBottom>Kart Önizleme</Typography>
              <Paper sx={{ p: 2, bgcolor: color1, borderRadius: 2 }}>
                <Box sx={{ width: '100%', height: 60, bgcolor: color2, borderRadius: 1, mb: 1 }} />
                <Typography sx={{ color: color2, fontWeight: 700 }}>Kart Başlığı</Typography>
                <Typography sx={{ color: color2, fontSize: 12, opacity: 0.8 }}>Alt açıklama metni</Typography>
              </Paper>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </Container>
  );
};

export default ColorComparator; 